import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { OtherPagesHeader } from '@/components/about/OtherPagesHeader';
import { Capacitor } from "@capacitor/core";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Helmet } from "react-helmet-async";
import { AboutFaqSection } from "@/components/about/AboutFaqSection";
import { AboutFooter } from "@/components/about/AboutFooter";
import { startGoogleWebOAuth } from "@/utils/googleWebAuth";

export default function Auth() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showSigninPassword, setShowSigninPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  const googleClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please enter both email and password",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        toast({
          title: "Success",
          description: "You've successfully signed in!",
        });
        navigate('/profile');
      }
    } catch (error: any) {
      toast({
        title: "Error signing in",
        description: error?.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please enter both email and password",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Registration successful! Please check your email to confirm your account.",
      });
      
      // If auto-confirm is enabled in Supabase, we can redirect to profile
      if (data.session) {
        navigate('/profile');
      }
    } catch (error: any) {
      toast({
        title: "Error signing up",
        description: error?.message || "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);

      // Store source in localStorage for redirect handling
      localStorage.setItem('metefy_oauth_source', 'auth');

      if (Capacitor.isNativePlatform()) {
        // NATIVE: Use Google's native sign-in SDK
        // This opens a native Google account picker - no browser needed!
        console.log('[Auth] Starting native Google Sign-In...');
        
        // Dynamically import GoogleAuth to avoid SSR issues
        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
        
        if (!googleClientId) {
          throw new Error('Google client ID is not configured');
        }

        try {
          // Initialize GoogleAuth with the Web Client ID (same one used by Supabase)
          await GoogleAuth.initialize({
            clientId: googleClientId,
            scopes: ['profile', 'email'],
            grantOfflineAccess: true
          });
        } catch (initError) {
          console.log('[Auth] GoogleAuth already initialized or init error:', initError);
        }
        
        // Sign in with Google natively
        const googleUser = await GoogleAuth.signIn();
        console.log('[Auth] Google Sign-In successful, got user:', googleUser.email);
        
        // Get the ID token from the Google sign-in result
        const idToken = googleUser.authentication.idToken;
        
        if (!idToken) {
          throw new Error('No ID token received from Google');
        }
        
        console.log('[Auth] Got ID token, signing into Supabase...');
        
        // Sign into Supabase using the Google ID token
        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: idToken,
        });
        
        if (error) {
          console.error('[Auth] Supabase signInWithIdToken error:', error);
          throw error;
        }
        
        console.log('[Auth] Supabase sign-in successful!');
        
        if (data.session) {
          // Successfully signed in - navigate to profile
          navigate('/profile');
        }
      } else {
        // WEB: Use custom Google OAuth flow that returns an ID token
        if (!googleClientId) {
          throw new Error('Google client ID is not configured');
        }

        startGoogleWebOAuth();
      }
    } catch (error: any) {
      console.error('[Auth] Google Sign-In Error:', error);
      // Clean up localStorage on error
      localStorage.removeItem('metefy_oauth_source');
      toast({
        title: "Error signing in with Google",
        description: error?.message || "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In | Metefy</title>
        <meta name="description" content="Sign in to your Metefy account to manage your lost and found items. Connect with people who found your belongings." />
        <meta property="og:title" content="Sign In | Metefy" />
        <meta property="og:description" content="Sign in to your Metefy account to manage your lost and found items. Connect with people who found your belongings." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.metefy.com/auth" />
        <meta property="og:image" content="https://www.metefy.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sign In | Metefy" />
        <meta name="twitter:description" content="Sign in to your Metefy account to manage your lost and found items. Connect with people who found your belongings." />
        <meta name="twitter:image" content="https://www.metefy.com/og-image.png" />
        <link rel="canonical" href="https://www.metefy.com/auth" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <OtherPagesHeader />
        <div className="flex h-[calc(100vh-4rem)] flex-col items-center justify-center bg-background px-4 sm:px-6 lg:px-8">
          <Card className="w-full max-w-md border-border shadow-lg bg-card">
            <CardHeader className="space-y-1">
              <div 
                className="w-16 h-16 mx-auto mb-2"
                style={{
                  maskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  backgroundColor: 'hsl(var(--primary))',
                  WebkitMaskImage: 'url(https://res.cloudinary.com/deqzsmrxq/image/upload/v1760986359/android-chrome-192x192_ffsgg9.png)',
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                }}
                role="img"
                aria-label="Metefy Logo"
              />
              <CardTitle className="text-2xl text-center text-foreground">Welcome to Metefy</CardTitle>
              <CardDescription className="text-center">
                <span className="block font-medium text-foreground/70 mt-1">For the smoothest experience, use Google Sign In below!</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Google Button */}
              <Button
                onClick={handleGoogleSignIn}
                variant="outline"
                className="w-full mb-6 border-2 border-primary text-foreground font-semibold text-base shadow whitespace-nowrap hover:bg-primary hover:text-primary-foreground transition-colors px-8 py-4"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 21 20" fill="none">
                      <g clipPath="url(#clip0_13183_10121)">
                        <path d="M20.3081 10.2303C20.3081 9.55056 20.253 8.86711 20.1354 8.19814H10.7031V12.0492H16.1046C15.8804 13.2911 15.1602 14.3898 14.1057 15.0879V17.5866H17.3282C19.2205 15.8449 20.3081 13.2728 20.3081 10.2303Z" fill="#3F83F8"></path>
                        <path d="M10.7019 20.0006C13.3989 20.0006 15.6734 19.1151 17.3306 17.5865L14.1081 15.0879C13.2115 15.6979 12.0541 16.0433 10.7056 16.0433C8.09669 16.0433 5.88468 14.2832 5.091 11.9169H1.76562V14.4927C3.46322 17.8695 6.92087 20.0006 10.7019 20.0006V20.0006Z" fill="#34A853"></path>
                        <path d="M5.08857 11.9169C4.66969 10.6749 4.66969 9.33008 5.08857 8.08811V5.51233H1.76688C0.348541 8.33798 0.348541 11.667 1.76688 14.4927L5.08857 11.9169V11.9169Z" fill="#FBBC04"></path>
                        <path d="M10.7019 3.95805C12.1276 3.936 13.5055 4.47247 14.538 5.45722L17.393 2.60218C15.5852 0.904587 13.1858 -0.0287217 10.7019 0.000673888C6.92087 0.000673888 3.46322 2.13185 1.76562 5.51234L5.08732 8.08813C5.87733 5.71811 8.09302 3.95805 10.7019 3.95805V3.95805Z" fill="#EA4335"></path>
                      </g>
                      <defs>
                        <clipPath id="clip0_13183_10121">
                          <rect width="20" height="20" fill="white" transform="translate(0.5)"></rect>
                        </clipPath>
                      </defs>
                    </svg>
                    Continue with Google
                  </>
                )}
              </Button>
              
              {/* Email Auth Section */}
              <Tabs defaultValue="signin" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-2 bg-muted">
                  <TabsTrigger value="signin" className="data-[state=active]:bg-background data-[state=active]:text-foreground">Sign In</TabsTrigger>
                  <TabsTrigger value="signup" className="data-[state=active]:bg-background data-[state=active]:text-foreground">Sign Up</TabsTrigger>
                </TabsList>
                <TabsContent value="signin">
                  <form onSubmit={handleEmailSignIn} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email" className="text-foreground">Email</Label>
                      <Input 
                        id="signin-email" 
                        type="email" 
                        placeholder="you@example.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                        className="border-border focus:border-primary focus:ring-0 bg-background text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password" className="text-foreground">Password</Label>
                      <div className="relative">
                        <Input 
                          id="signin-password" 
                          type={showSigninPassword ? "text" : "password"}
                          placeholder="••••••••" 
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={loading}
                          className="pr-10 border-border focus:border-primary focus:ring-primary bg-background text-foreground"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground hover:text-foreground"
                          tabIndex={-1}
                          onClick={() => setShowSigninPassword((s) => !s)}
                          aria-label={showSigninPassword ? "Hide password" : "Show password"}
                        >
                          {showSigninPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-foreground text-background hover:bg-foreground/90 font-normal" disabled={loading}>
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                </TabsContent>
                <TabsContent value="signup">
                  <form onSubmit={handleEmailSignUp} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-email" className="text-foreground">Email</Label>
                      <Input 
                        id="signup-email" 
                        type="email" 
                        placeholder="you@example.com" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                        className="border-border focus:border-primary focus:ring-0 bg-background text-foreground"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password" className="text-foreground">Password</Label>
                      <div className="relative">
                        <Input 
                          id="signup-password" 
                          type={showSignupPassword ? "text" : "password"}
                          placeholder="••••••••" 
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          disabled={loading}
                          className="pr-10 border-border focus:border-primary focus:ring-primary bg-background text-foreground"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground hover:text-foreground"
                          tabIndex={-1}
                          onClick={() => setShowSignupPassword((s) => !s)}
                          aria-label={showSignupPassword ? "Hide password" : "Show password"}
                        >
                          {showSignupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-foreground text-background hover:bg-foreground/90 font-normal" disabled={loading}>
                      {loading ? "Signing up..." : "Sign Up"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
            <CardFooter className="flex flex-col items-center">
              <p className="text-sm text-foreground/70 mt-2">
                By continuing, you agree to Metefy's <Link to="/terms-and-conditions" className="text-primary hover:underline">Terms of Service</Link> <span className="text-foreground/70">and</span> <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
              </p>
            </CardFooter>
            {/* Admin Button */}
            {/* {!showEmailAuth && (
              <div className="mt-4 px-6 pb-6">
                <Dialog open={isDevDialogOpen} onOpenChange={handleDevDialogOpenChange}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-center text-sm text-foreground border-border hover:bg-muted hover:text-foreground"
                    >
                      Developer Mode
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="text-foreground">Unlock Developer Mode</DialogTitle>
                      <DialogDescription className="text-muted-foreground">
                        Enter the admin password to enable developer mode.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="mt-4 space-y-4">
                      <Label htmlFor="admin-password-dialog" className="text-sm text-foreground text-left">
                        Admin Password
                      </Label>
                      <Input 
                        id="admin-password-dialog"
                        type="password" 
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        placeholder="Enter admin password"
                        className="text-sm w-full border-border focus:border-primary focus:ring-primary bg-background text-foreground"
                        maxLength={8}
                      />
                    </div>
                    <DialogFooter>
                      <Button onClick={handleAdminAccess} className="w-full text-sm bg-primary text-primary-foreground hover:bg-primary/90">
                        Unlock Developer Mode
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )} */}
          </Card>
        </div>
        <AboutFaqSection />
        <AboutFooter />
      </div>
    </>
  );
}
